import type { TenantLicensePlan, TenantLicenseStatus } from "@shudian/contracts";

export const LICENSE_PLAN_OPTIONS: Array<{ value: TenantLicensePlan; label: string; shortLabel: string }> = [
  { value: "monthly", label: "月套餐", shortLabel: "月" },
  { value: "quarterly", label: "季度套餐", shortLabel: "季度" },
  { value: "yearly", label: "年套餐", shortLabel: "年" }
];

const LICENSE_PLAN_LABELS: Record<TenantLicensePlan, string> = Object.fromEntries(
  LICENSE_PLAN_OPTIONS.map((option) => [option.value, option.label])
) as Record<TenantLicensePlan, string>;

export function formatLicensePlan(plan: string): string {
  return LICENSE_PLAN_LABELS[plan as TenantLicensePlan] ?? "月套餐";
}

export function addLicensePlanDuration(plan: TenantLicensePlan, base: Date): Date {
  const next = new Date(base.getTime());
  if (plan === "yearly") {
    next.setFullYear(next.getFullYear() + 1);
    return next;
  }
  next.setMonth(next.getMonth() + (plan === "quarterly" ? 3 : 1));
  return next;
}

export function renewLicenseExpiresAt(plan: TenantLicensePlan, currentExpiresAt: string): string {
  const now = new Date();
  const current = new Date(currentExpiresAt);
  const base = Number.isFinite(current.getTime()) && current.getTime() > now.getTime() ? current : now;
  return addLicensePlanDuration(plan, base).toISOString();
}

export function formatDateTimeInput(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function dateTimeInputToIso(value: string): string {
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : value;
}

export function licenseStatusLabel(status: string): string {
  if (status === "active") return "可用";
  if (status === "disabled") return "停用";
  if (status === "expired") return "已过期";
  return status;
}

export function licenseStatusTone(status: string): "success" | "warning" | "danger" | "neutral" {
  if (status === "active") return "success";
  if (status === "expired") return "warning";
  if (status === "disabled") return "danger";
  return "neutral";
}

export function getLicenseExpiryState(
  expiresAt: string,
  status?: TenantLicenseStatus | string,
  now = new Date()
): { label: string; tone: "success" | "warning" | "danger" | "neutral"; daysRemaining: number } {
  const expiry = new Date(expiresAt);
  if (!Number.isFinite(expiry.getTime())) {
    return { label: "到期时间无效", tone: "danger", daysRemaining: 0 };
  }
  const msRemaining = expiry.getTime() - now.getTime();
  const daysRemaining = Math.ceil(msRemaining / 86_400_000);
  if (status === "disabled") {
    return { label: "已停用", tone: "danger", daysRemaining };
  }
  if (msRemaining <= 0 || status === "expired") {
    return { label: "已过期", tone: "danger", daysRemaining };
  }
  if (daysRemaining <= 7) {
    return { label: `剩余 ${daysRemaining} 天`, tone: "warning", daysRemaining };
  }
  return { label: `剩余 ${daysRemaining} 天`, tone: "success", daysRemaining };
}
